export type Vertical = "travel" | "education" | "healthcare";

export type SuggestArgs = {
  vertical: Vertical;
  amountAED: number;
  context?: Record<string, any>;
};

export type Plan = {
  id: string;
  label: string;
  breakdown: {
    bnplAED?: number;
    bankFinanceAED?: number;
    upfrontAED?: number;
  };
  aprPercent?: number;          // Informational (if known)
  tenureMonths?: number;        // Overall average tenure
  notes?: string[];
  redirectUrl?: string;         // External partner link
};

const BNPL_MIN = 2000;
const BNPL_SWEET = 20000;       // good band for BNPL
const ABS_MAX = 200000;         // overall ceiling you've referenced

const BNPL_PROVIDERS = [
  { id: "bnpl-tabby", name: "Tabby (example)", maxAED: 20000, tenures: [3,6,12], redirect: "/partners/tabby" },
  { id: "bnpl-tamara", name: "Tamara (example)", maxAED: 20000, tenures: [3,6,12], redirect: "/partners/tamara" },
];

const BANK_LENDERS = [
  { id: "fin-xyz", name: "Finance House (example)", maxAED: ABS_MAX, tenures: [12,24,36], redirect: "/partners/finance-house" },
  { id: "bank-abc", name: "Bank ABC Personal Finance (example)", maxAED: ABS_MAX, tenures: [12,24,48], redirect: "/partners/bank-abc" },
];

export function suggestFinancePlans(args: SuggestArgs): Plan[] {
  const { vertical, amountAED, context = {} } = args;
  const plans: Plan[] = [];

  // If amount is tiny, encourage upfront
  if (amountAED < BNPL_MIN) {
    plans.push({
      id: "upfront",
      label: "Pay upfront",
      breakdown: { upfrontAED: amountAED },
      notes: ["Amount is below typical BNPL minimums."]
    });
    return plans;
  }

  // Education logic with level-specific recommendations
  if (vertical === "education") {
    const termCount: 2 | 3 = context.termCount ?? 3;
    const level: "early" | "primary" | "secondary" | "higher" = context.level ?? "primary";
    const enrolled = !!context.enrolled;

    // Per-term/semester fee
    const perBill = Math.round(amountAED / termCount);
    const bnplChunk = Math.min(perBill, BNPL_SWEET);
    const remainder = amountAED - bnplChunk;

    // Default recommendation varies by level
    if (level === "higher") {
      // Universities: 2 semesters typical, larger bills
      plans.push({
        id: "edu-higher-mix",
        label: "BNPL for upcoming semester + finance the rest",
        breakdown: { bnplAED: bnplChunk, bankFinanceAED: Math.max(0, remainder) },
        tenureMonths: remainder > 0 ? 12 : 6,
        notes: [
          enrolled ? "Enrolment confirmed." : "Enrolment to be confirmed.",
          `BNPL covers next semester (~AED ${bnplChunk.toLocaleString()}).`,
          remainder > 0 ? `Finance covers remainder (~AED ${remainder.toLocaleString()}).` : "No remainder to finance."
        ],
        redirectUrl: remainder > 0 ? BANK_LENDERS[0].redirect : BNPL_PROVIDERS[0].redirect
      });

      plans.push({
        id: "edu-higher-full-finance",
        label: "Finance full annual tuition",
        breakdown: { bankFinanceAED: amountAED },
        tenureMonths: 12,
        notes: ["Lower monthly, single approval for the academic year."],
        redirectUrl: BANK_LENDERS[1].redirect
      });

      if (amountAED <= BNPL_SWEET) {
        plans.push({
          id: "edu-higher-bnpl-only",
          label: "BNPL the full annual tuition",
          breakdown: { bnplAED: amountAED },
          tenureMonths: 6,
          notes: ["Fast checkout; limited tenor vs finance."],
          redirectUrl: BNPL_PROVIDERS[1].redirect
        });
      }

      return plans;
    }

    // Early/Primary/Secondary
    plans.push({
      id: "edu-mix-1",
      label: "BNPL for next term + finance the remainder",
      breakdown: { bnplAED: bnplChunk, bankFinanceAED: Math.max(0, remainder) },
      tenureMonths: remainder > 0 ? 12 : 6,
      notes: [
        enrolled ? "Enrolment confirmed." : "Enrolment to be confirmed.",
        `BNPL covers the upcoming term (~AED ${bnplChunk.toLocaleString()}).`,
        remainder > 0 ? `Finance the rest (~AED ${remainder.toLocaleString()}).` : "No remainder to finance."
      ],
      redirectUrl: remainder > 0 ? BANK_LENDERS[0].redirect : BNPL_PROVIDERS[0].redirect
    });

    plans.push({
      id: "edu-full-finance",
      label: "Finance full annual tuition",
      breakdown: { bankFinanceAED: amountAED },
      tenureMonths: 12,
      notes: ["Lower monthly, single approval for the year."],
      redirectUrl: BANK_LENDERS[1].redirect
    });

    if (amountAED <= BNPL_SWEET) {
      plans.push({
        id: "edu-bnpl-only",
        label: "BNPL the full annual tuition",
        breakdown: { bnplAED: amountAED },
        tenureMonths: 6,
        notes: ["Faster checkout; shorter tenor."],
        redirectUrl: BNPL_PROVIDERS[1].redirect
      });
    }

    return plans;
  }

  // Healthcare logic: urgency + bill size
  if (vertical === "healthcare") {
    const urgency = context.urgency ?? "Elective";

    // Plan A: BNPL-only if in sweet spot
    if (amountAED <= BNPL_SWEET) {
      plans.push({
        id: "hc-bnpl",
        label: "BNPL (fast approval)",
        breakdown: { bnplAED: amountAED },
        tenureMonths: urgency === "Urgent" ? 3 : 6,
        notes: [
          "Great for outpatient or small inpatient bills.",
          urgency === "Urgent" ? "Shorter tenor to maximise instant approval chances." : "Standard BNPL tenors available."
        ],
        redirectUrl: BNPL_PROVIDERS[0].redirect
      });
    }

    // Plan B: Mix for mid/high bills
    if (amountAED > BNPL_SWEET && amountAED <= ABS_MAX) {
      const bnplPortion = BNPL_SWEET; // cap BNPL to sweet spot
      const financePortion = amountAED - bnplPortion;

      plans.push({
        id: "hc-mix",
        label: "BNPL + Finance mix",
        breakdown: { bnplAED: bnplPortion, bankFinanceAED: financePortion },
        tenureMonths: 12,
        notes: [
          `BNPL up to ~AED ${bnplPortion.toLocaleString()} for immediate needs.`,
          `Finance remaining ~AED ${financePortion.toLocaleString()} for lower monthly payments.`
        ],
        redirectUrl: BANK_LENDERS[0].redirect
      });
    }

    // Plan C: Full finance (bigger surgeries)
    if (amountAED > 30000) {
      plans.push({
        id: "hc-finance-full",
        label: "Finance full amount",
        breakdown: { bankFinanceAED: amountAED },
        tenureMonths: urgency === "Urgent" ? 12 : 24,
        notes: ["Lower monthly instalments; longer tenor available."],
        redirectUrl: BANK_LENDERS[1].redirect
      });
    }

    // Fallback: upfront if nothing else matched
    if (!plans.length) {
      plans.push({
        id: "hc-upfront",
        label: "Pay upfront",
        breakdown: { upfrontAED: amountAED },
        notes: ["No financing recommended for this amount."]
      });
    }

    return plans;
  }

  // Travel logic (adding this for completeness)
  if (vertical === "travel") {
    const travelType = context.travelType ?? "leisure";
    
    // Plan A: BNPL-only if in sweet spot
    if (amountAED <= BNPL_SWEET) {
      plans.push({
        id: "travel-bnpl",
        label: "BNPL (quick approval)",
        breakdown: { bnplAED: amountAED },
        tenureMonths: 6,
        notes: ["Perfect for vacation packages and short trips."],
        redirectUrl: BNPL_PROVIDERS[0].redirect
      });
    }

    // Plan B: Mix for expensive trips
    if (amountAED > BNPL_SWEET && amountAED <= ABS_MAX) {
      const bnplPortion = BNPL_SWEET;
      const financePortion = amountAED - bnplPortion;

      plans.push({
        id: "travel-mix",
        label: "BNPL + Finance mix",
        breakdown: { bnplAED: bnplPortion, bankFinanceAED: financePortion },
        tenureMonths: 12,
        notes: [
          `BNPL for immediate booking (~AED ${bnplPortion.toLocaleString()}).`,
          `Finance remaining amount for lower monthly payments.`
        ],
        redirectUrl: BANK_LENDERS[0].redirect
      });
    }

    // Plan C: Full finance for luxury trips
    if (amountAED > 30000) {
      plans.push({
        id: "travel-finance-full",
        label: "Finance full trip",
        breakdown: { bankFinanceAED: amountAED },
        tenureMonths: 18,
        notes: ["Extended payment terms for luxury travel experiences."],
        redirectUrl: BANK_LENDERS[1].redirect
      });
    }

    return plans;
  }

  // Generic fallback
  return [{
    id: "generic-bnpl",
    label: "BNPL (default)",
    breakdown: { bnplAED: Math.min(amountAED, BNPL_SWEET) },
    tenureMonths: 6,
    notes: ["Default option."],
    redirectUrl: BNPL_PROVIDERS[0].redirect
  }];
}